import { useState } from "react";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FilePicker } from "@/components/upload/FilePicker";
import { BatchFiles } from "@/components/quote/BatchProgress";
import { startBatch, useBatch } from "@/lib/useBatch";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function AddFilesDialog({ open, onClose, prepare, onMerged }) {
  const { setCredits } = useAuth();
  const [batchId, setBatchId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const merge = async (b) => {
    if (!b.files.some((f) => f.status === "done") || b.applied) return;
    if (b.files.some((f) => f.status === "failed")) return;
    await doMerge(b);
  };
  const doMerge = async (b) => {
    try {
      const { data } = await api.post(`/quotes/${quoteId}/merge`, { batch_id: b.id });
      onMerged(data);
      setBatchId(null);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const { batch, retry } = useBatch(batchId, merge);
  const start = async (files) => {
    setBusy(true);
    try {
      const saved = await prepare();
      if (!saved) return;
      setQuoteId(saved.id);
      const data = await startBatch(files, saved.id);
      setCredits(data.balance);
      data.rejected.forEach((r) => toast.error(`${r.filename}: ${r.error}`));
      setBatchId(data.batch_id);
    } catch (e) {
      if (e.response?.status === 402) setCredits(0);
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const allFailed = batch?.finished && !batch.files.some((f) => f.status === "done");
  const someFailed = batch?.finished && !allFailed && batch.files.some((f) => f.status === "failed");
  const canRetryFree = someFailed && !batch.refunded && batch.files.some((f) => f.status === "failed" && !/DXF/.test(f.error || ""));
  const close = () => { if (!batchId || batch?.finished) { setBatchId(null); onClose(); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="add-files-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-[#F2F2F7]">
        <DialogHeader>
          <DialogTitle>Add files to this quote</DialogTitle>
          <DialogDescription>New items merge in. Plan (nominal) lines you haven't edited are replaced by schedule lines; edited lines are never changed.</DialogDescription>
        </DialogHeader>
        {!batchId && <FilePicker compact busy={busy} onStart={start} title="Drop schedules or plans" startLabel="Add to quote" />}
        {batchId && batch?.files && <BatchFiles batch={batch} onRetry={retry} />}
        {someFailed && (
          <div data-testid="add-files-partial" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <p className="text-sm text-slate-200">Some files couldn't be read. {canRetryFree ? "Try them again (free), or add what was read." : "Add what was read, or try the failed files again later (1 credit)."}</p>
            <button data-testid="add-files-merge-now-button" onClick={() => doMerge(batch)}
              className="rounded-full bg-amber-500 px-4 py-1.5 text-xs font-semibold text-slate-950 hover:bg-amber-400">Add what was read</button>
          </div>
        )}
        {allFailed && (
          <p data-testid="add-files-nothing-found" className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-200">
            Nothing could be read from these files{batch.refunded ? ". Your credit was refunded." : "."} Try again or close.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function MergeResult({ result, onUndo }) {
  const [busy, setBusy] = useState(false);
  return (
    <div data-testid="merge-result-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
      <p data-testid="merge-result-text" className="text-sm text-slate-200">
        {result.text}{result.flagged ? ` · ${result.flagged} possible duplicate${result.flagged === 1 ? "" : "s"} to review` : ""}
      </p>
      <button data-testid="merge-undo-button" disabled={busy} onClick={async () => { setBusy(true); await onUndo(); setBusy(false); }}
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-200 disabled:opacity-50">
        <Undo2 className="h-3.5 w-3.5" /> Undo
      </button>
    </div>
  );
}
